import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import { RecaptchaVerifier, signInWithPhoneNumber, signOut } from 'firebase/auth';
import { auth, persistenceReady } from '../lib/firebase';
import { hideVisibleRecaptcha as hideRecaptchaContainer } from '../lib/recaptchaLifecycle';
import { getSelectedTenant, ACTIVE_TENANT_HEADER } from '../lib/tenantSession';
import { tenantHeaders } from '../lib/tenantSession';

const CRMAuthContext = createContext();

export const useCRMAuth = () => {
  const context = useContext(CRMAuthContext);

  // IMPORTANT:
  // If a component calls useCRMAuth() outside <CRMAuthProvider>, context will be undefined.
  // Return a safe empty object so destructuring does NOT hard-crash the app.
  return context || {};
};

export const CRMAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(null);

  // OTP flow (Firebase confirmationResult)
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [pendingPhoneNumber, setPendingPhoneNumber] = useState(null);

  // Recaptcha
  const recaptchaReadyRef = useRef(false);
  // Single-flight: a burst of conclusive 401s must invalidate the session exactly once, not sign
  // the user out repeatedly or race the redirect.
  const sessionInvalidationRef = useRef(false);

  // DEV: if you want to bypass rendering loops during local dev only.
  // NOTE: Firebase test phone numbers still require a verifier in web flows,
  // but this prevents repeated re-render/init issues during dev.
  const isDev = process.env.NODE_ENV === 'development';

  // Get backend URL (same-origin via nginx / Cloud Run)
  // Architecture: CRM uses Firebase ID Token ONLY for /api/v1/* calls.
  const getBackendUrl = () => {
    const raw = (process.env.REACT_APP_API_BASE || process.env.REACT_APP_BACKEND_URL || '').trim();
    // Allow same-origin relative calls when unset
    return raw.endsWith('/') ? raw.slice(0, -1) : raw;
  };

  // Ensure a reCAPTCHA verifier for Firebase Phone Auth.
  // P0 auth recovery: supports an INVISIBLE verifier (fast path) and a VISIBLE fallback
  // (user-solved checkbox) for when the invisible challenge can't mint a token.
  // Always creates a FRESH verifier in the requested container (no stale reuse).
  const ensureRecaptcha = async (visible = false) => {
    if (typeof window === 'undefined') return null;

    const mode = visible ? 'visible' : 'invisible';
    // DR-013: REUSE a ready verifier of the same mode instead of clearing+recreating on every OTP
    // send/resend (which churned reCAPTCHA and could double-init under concurrent sends). Only recreate
    // when the mode changes or the verifier was cleared (e.g. by the expired-callback).
    if (window.__crmRecaptchaVerifier && recaptchaReadyRef.current && window.__crmRecaptchaMode === mode) {
      return window.__crmRecaptchaVerifier;
    }
    // Mode change or no ready verifier -> clear any stale one so a fresh one renders in the right container.
    if (window.__crmRecaptchaVerifier) {
      try { window.__crmRecaptchaVerifier.clear(); } catch (e) { /* ignore */ }
      window.__crmRecaptchaVerifier = null;
      recaptchaReadyRef.current = false;
    }

    const containerId = visible ? 'crm-recaptcha-visible' : 'crm-recaptcha-container';
    const el = document.getElementById(containerId);
    if (!el) { console.error('[CRMAuth] reCAPTCHA container missing:', containerId); return null; }
    if (visible) { try { el.style.display = 'block'; el.innerHTML = ''; } catch (e) {} }

    try {
      console.log('[CRMAuth] reCAPTCHA verifier creating, mode=', visible ? 'visible' : 'invisible');
      const verifier = new RecaptchaVerifier(auth, containerId, {
        size: visible ? 'normal' : 'invisible',
        callback: () => { console.log('[CRMAuth] reCAPTCHA callback fired'); },
        'expired-callback': () => {
          try { window.__crmRecaptchaVerifier?.clear(); } catch (e) { /* ignore */ }
          window.__crmRecaptchaVerifier = null;
          recaptchaReadyRef.current = false;
        }
      });

      console.log('[CRMAuth] reCAPTCHA render started, mode=', visible ? 'visible' : 'invisible');
      await verifier.render();

      window.__crmRecaptchaVerifier = verifier;
      window.__crmRecaptchaMode = mode;
      recaptchaReadyRef.current = true;
      console.log('[CRMAuth] reCAPTCHA verifier created');
      return verifier;
    } catch (error) {
      console.error('[CRMAuth] Failed to initialize reCAPTCHA verifier:', error);
      try { window.__crmRecaptchaVerifier?.clear(); } catch (e) { /* ignore */ }
      window.__crmRecaptchaVerifier = null;
      recaptchaReadyRef.current = false;
      return null;
    }
  };

  // P0 auth-recovery helpers
  const withTimeout = (promise, ms, label) => Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(
      () => reject(Object.assign(new Error(label || 'timed out'), { code: 'auth/timeout' })), ms)),
  ]);

  const friendlyAuthError = (e) => {
    const c = (e && e.code) || '';
    if (c.includes('too-many-requests')) return 'Too many attempts. Please wait a few minutes, then try again.';
    if (c.includes('captcha')) return 'Verification failed. Please complete the verification checkbox and try again.';
    if (c.includes('timeout')) return 'Verification did not complete. Please use the visible verification box that appeared.';
    if (c.includes('invalid-phone')) return 'Please enter a valid phone number.';
    if (c.includes('quota')) return 'SMS quota exceeded. Please try again later.';
    return (e && e.message) || 'Failed to send the verification code.';
  };

  // Hide via CSS only (extracted + unit-tested in lib/recaptchaLifecycle). Do NOT wipe innerHTML:
  // the Enterprise reCAPTCHA script keeps a reference to its rendered nodes and asynchronously reads
  // `.style` on them after a successful OTP — removing them caused a null-ref in recaptcha__en.js.
  // verifier.clear() (in ensureRecaptcha before a re-create) is the authoritative teardown.
  const hideVisibleRecaptcha = () => { hideRecaptchaContainer(document); };

  // Single OTP-send attempt. invisible = 30s timeout; visible = 120s (waits for user to solve).
  const doSignIn = async (phoneNumber, visible) => {
    const verifier = await ensureRecaptcha(visible);
    if (!verifier) throw Object.assign(new Error('reCAPTCHA not ready'), { code: 'auth/recaptcha-not-ready' });
    console.log('[CRMAuth] signInWithPhoneNumber started, visible=', visible);
    const result = await withTimeout(
      signInWithPhoneNumber(auth, phoneNumber, verifier),
      visible ? 120000 : 30000,
      visible ? 'Verification timed out' : 'reCAPTCHA/OTP send timed out'
    );
    console.log('[CRMAuth] signInWithPhoneNumber resolved');
    if (!result || typeof result.confirm !== 'function') throw new Error('Firebase did not return a valid confirmationResult');
    return result;
  };

  // Initialize authentication state — wait for Firebase to restore session
  useEffect(() => {
    // First, optimistically load from localStorage so UI doesn't flash blank
    const storedToken =
      localStorage.getItem('firebase_token') ||
      localStorage.getItem('idToken') ||
      localStorage.getItem('crm_access_token');
    const storedUserData = localStorage.getItem('crm_user_data');

    if (storedToken && storedUserData) {
      try {
        const parsedUser = JSON.parse(storedUserData);
        setAccessToken(storedToken);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('firebase_token');
        localStorage.removeItem('idToken');
        localStorage.removeItem('crm_access_token');
        localStorage.removeItem('crm_user_data');
      }
    }

    // Then listen for Firebase auth state — refresh token when session restores
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      // SuperAdmin plane isolation: the owner portal (/superadmin-portal/*) uses SuperAdminAuthContext
      // (OTP/JWT), a SEPARATE auth system. The tenant/Business Firebase listener must NOT execute or
      // clear session state there — doing so races the owner session. No-op on SuperAdmin routes.
      if (typeof window !== 'undefined' &&
          window.location.pathname.startsWith('/superadmin-portal')) {
        return;
      }
      if (firebaseUser) {
        // SuperAdmin session active — CRM must not hydrate its own identity.
        // SuperAdmin and CRM are separate auth systems that must not coexist.
        if (localStorage.getItem('superadmin_session')) {
          setUser(null);
          setIsAuthenticated(false);
          setAccessToken(null);
          localStorage.removeItem('firebase_token');
          localStorage.removeItem('idToken');
          localStorage.removeItem('crm_access_token');
          localStorage.removeItem('crm_user_data');
        } else {
          try {
            const freshToken = await firebaseUser.getIdToken(true);
            localStorage.setItem('firebase_token', freshToken);
            localStorage.setItem('crm_access_token', freshToken);
            setAccessToken(freshToken);

            // Preserve existing user data (roles, etc.) but ensure we have auth
            const existingData = localStorage.getItem('crm_user_data');
            if (existingData) {
              try {
                const parsed = JSON.parse(existingData);
                setUser(parsed);
              } catch (e) {
                // ignore parse error
              }
            }
            setIsAuthenticated(true);
          } catch (error) {
            console.error('Error refreshing token on auth state change:', error);
          }
        }
      } else {
        // A null user is NOT automatically proof of sign-out. On a normal page reload the SDK can
        // report null while it is still restoring the persisted session, and this branch used to
        // clear crm_access_token + crm_user_data immediately — so a browser refresh logged the user
        // out and demanded a fresh OTP. (crm_user_data also carries the selected brandId, so the
        // tenant context died with it.) This same null window was already known here: getFreshIdToken
        // awaits `initialAuthReady` to avoid "Bearer null", and the OTP path notes that
        // "onAuthStateChanged(null) raced and cleared it during OTP confirmation". The readers were
        // hardened; the CLEARER never was.
        //
        // Wait for persistence to be installed, then re-check. After that a null really is signed
        // out, so genuine logout, expiry and the 401 invalidation path all still clear exactly as
        // before — nothing is weakened, and no token is kept alive that Firebase has discarded.
        try { await persistenceReady; } catch { /* fall through to the re-check */ }
        if (auth.currentUser) {
          setIsLoading(false);
          return;                       // restored mid-flight — this null was never a sign-out
        }
        console.log('[CRMAuth] onAuthStateChanged: null — clearing session. pathname:', window.location.pathname);
        setUser(null);
        setIsAuthenticated(false);
        setAccessToken(null);
        localStorage.removeItem('firebase_token');
        localStorage.removeItem('idToken');
        localStorage.removeItem('crm_access_token');
        localStorage.removeItem('crm_user_data');
      }
      // Always mark loading as done once Firebase has checked
      setIsLoading(false);
    });

    // Safety timeout — don't block UI forever if Firebase is slow
    const timeout = setTimeout(() => {
      setIsLoading(false);
    }, 6000);

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  /**
   * NOTE (ARCHITECTURE ALIGNMENT):
   * - NO backend OTP
   * - NO backend JWT issuance
   * - OTP must be triggered ONLY via Firebase Phone Auth (signInWithPhoneNumber)
   * - After OTP verify, obtain Firebase ID Token and use it as Bearer token for /api/v1/* routes
   */

  const registerPhoneNumber = async (phoneNumber, deviceId) => {
    try {
      if (!phoneNumber) {
        throw new Error('Phone number is required');
      }

      setPendingPhoneNumber(phoneNumber);
      const sendResult = await loginPhoneNumber(phoneNumber);
      return sendResult;
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: error.message || 'Registration failed'
      };
    }
  };

  const loginPhoneNumber = async (phoneNumber) => {
    // Do not log the raw phone number (PII); a plain correlation marker is enough.
    console.log('[CRMAuth] loginPhoneNumber CALLED');
    if (!phoneNumber) return { success: false, error: 'Phone number is required' };
    setPendingPhoneNumber(phoneNumber);

    // 1) Fast path: invisible reCAPTCHA with a 30s timeout (never hang forever).
    try {
      const result = await doSignIn(phoneNumber, false);
      setConfirmationResult(result);
      hideVisibleRecaptcha();
      return { success: true, message: 'OTP sent successfully', expiresIn: 0 };
    } catch (error) {
      console.warn('[CRMAuth] invisible reCAPTCHA failed/timeout:', error && error.code, error && error.message, '(timeout fired:', (error && error.code) === 'auth/timeout', ')');
      try { window.__crmRecaptchaVerifier?.clear(); } catch (e) {}
      window.__crmRecaptchaVerifier = null;
      recaptchaReadyRef.current = false;

      // 2) Visible reCAPTCHA fallback: shows a checkbox the user solves → mints a token the
      //    invisible flow couldn't → OTP proceeds (incl. Firebase test numbers via 555555).
      try {
        console.log('[CRMAuth] falling back to VISIBLE reCAPTCHA — complete the checkbox to continue');
        const result = await doSignIn(phoneNumber, true);
        setConfirmationResult(result);
        hideVisibleRecaptcha();
        return { success: true, message: 'OTP sent successfully', expiresIn: 0, usedVisibleRecaptcha: true };
      } catch (err2) {
        console.error('[CRMAuth] signInWithPhoneNumber rejected (visible fallback):', err2 && err2.code, err2 && err2.message);
        try { window.__crmRecaptchaVerifier?.clear(); } catch (e) {}
        window.__crmRecaptchaVerifier = null;
        recaptchaReadyRef.current = false;
        hideVisibleRecaptcha();
        return { success: false, error: friendlyAuthError(err2), code: err2 && err2.code, needsVisibleRecaptcha: true };
      }
    }
  };

  const verifyOTP = async (phoneNumber, otpCode, deviceId) => {
    try {
      if (!otpCode) {
        throw new Error('OTP code is required');
      }

      if (phoneNumber) setPendingPhoneNumber(phoneNumber);

      if (!confirmationResult) {
        throw new Error('OTP session not found. Please click Send OTP again.');
      }

      const credential = await confirmationResult.confirm(otpCode);
      const idToken = await credential.user.getIdToken(true);

      const freshPhone = credential.user.phoneNumber || pendingPhoneNumber || phoneNumber || null;
      // Defensive parse — crm_user_data may be absent, empty, or corrupted (e.g. if
      // onAuthStateChanged(null) raced and cleared it during OTP confirmation).
      const existingCrmData = (() => {
        try { return JSON.parse(localStorage.getItem('crm_user_data') || '{}'); } catch { return {}; }
      })();
      // Only carry enriched fields (brandId, role, etc.) forward when re-authenticating
      // the SAME Firebase uid. A different uid means a fresh signup — start with a clean
      // object so stale session data never contaminates the new onboarding flow.
      const isSameUser = Boolean(existingCrmData.uid && existingCrmData.uid === credential.user.uid);
      const crmUser = isSameUser
        ? { ...existingCrmData, uid: credential.user.uid, phoneNumber: freshPhone }
        // Fresh session: always include customer_id so conversation creation never sends undefined.
        // Signup flows (business/customer) will overwrite role/name/brandId after this returns.
        : { uid: credential.user.uid, customer_id: credential.user.uid, phoneNumber: freshPhone };
      // Do not log Firebase UIDs, phone numbers, or the merged auth object (PII). Keep only the
      // non-identifying same-user signal for debugging the merge path.
      console.log('[CRMAuth] verifyOTP merge:', { isSameUser });

      localStorage.setItem('firebase_token', idToken);
      localStorage.setItem('crm_access_token', idToken);
      localStorage.setItem('crm_user_data', JSON.stringify(crmUser));

      setAccessToken(idToken);
      setUser(crmUser);
      setIsAuthenticated(true);
      setConfirmationResult(null);

      return {
        success: true,
        message: 'Login successful',
        token: idToken,
        user: crmUser
      };
    } catch (error) {
      console.error('OTP verification error:', error);
      return {
        success: false,
        error: error.message || 'OTP verification failed'
      };
    }
  };

  const resendOTP = async (phoneNumber) => {
    const targetPhone = phoneNumber || pendingPhoneNumber;
    if (!targetPhone) return { success: false, error: 'Phone number is required to resend OTP' };
    setPendingPhoneNumber(targetPhone);
    try {
      const result = await doSignIn(targetPhone, false);
      setConfirmationResult(result);
      hideVisibleRecaptcha();
      return { success: true, message: 'OTP resent successfully' };
    } catch (error) {
      console.warn('[CRMAuth] resend invisible failed/timeout:', error && error.code);
      try { window.__crmRecaptchaVerifier?.clear(); } catch (e) {}
      window.__crmRecaptchaVerifier = null;
      recaptchaReadyRef.current = false;
      try {
        const result = await doSignIn(targetPhone, true);
        setConfirmationResult(result);
        hideVisibleRecaptcha();
        return { success: true, message: 'OTP resent successfully', usedVisibleRecaptcha: true };
      } catch (err2) {
        console.error('[CRMAuth] resend visible fallback failed:', err2 && err2.code, err2 && err2.message);
        try { window.__crmRecaptchaVerifier?.clear(); } catch (e) {}
        window.__crmRecaptchaVerifier = null;
        recaptchaReadyRef.current = false;
        hideVisibleRecaptcha();
        return { success: false, error: friendlyAuthError(err2), code: err2 && err2.code };
      }
    }
  };

  let refreshPromise = null;

  const refreshAccessToken = async () => {
    try {
      // Prevent multiple refresh calls
      if (refreshPromise) {
        return await refreshPromise;
      }

      refreshPromise = new Promise(async (resolve) => {
        try {
          const currentUser = await new Promise((resolveUser) => {
            if (auth.currentUser) {
              resolveUser(auth.currentUser);
              return;
            }

            const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
              unsubscribe();
              resolveUser(firebaseUser);
            });

            setTimeout(() => resolveUser(null), 5000);
          });

          if (!currentUser) {
            console.warn("No Firebase user found — cannot refresh token");
            refreshPromise = null;
            resolve(null);
            return;
          }

          const newToken = await currentUser.getIdToken(true);

          localStorage.setItem("firebase_token", newToken);
          localStorage.setItem("crm_access_token", newToken);

          setAccessToken(newToken);

          refreshPromise = null;
          resolve(newToken);
        } catch (error) {
          console.error("Token refresh error:", error);
          refreshPromise = null;
          resolve(null);
        }
      });

      return await refreshPromise;
    } catch (error) {
      console.error("Token refresh error:", error);
      refreshPromise = null;
      return null;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);

      localStorage.removeItem('firebase_token');
      localStorage.removeItem('idToken');
      localStorage.removeItem('crm_access_token');
      localStorage.removeItem('crm_user_data');
      localStorage.removeItem('user');

      setUser(null);
      setAccessToken(null);
      setIsAuthenticated(false);

      setConfirmationResult(null);
      setPendingPhoneNumber(null);

      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message || 'Logout failed' };
    }
  };

  const makeAuthenticatedRequest = async (url, options = {}) => {
    try {
      // ALWAYS get live firebase user
      const currentUser = await new Promise((resolve) => {
        if (auth.currentUser) {
          resolve(auth.currentUser);
          return;
        }

        const unsubscribe = auth.onAuthStateChanged((user) => {
          unsubscribe();
          resolve(user);
        });

        setTimeout(() => resolve(null), 5000);
      });

      let token = null;

      if (currentUser) {
        token = await currentUser.getIdToken();
      }

      // PMT1 active-tenant context: a multi-membership user's identity does not identify the business
      // on screen, so every protected call states which tenant it is acting in. Selection only - the
      // server re-verifies membership in this tenant and fails closed on a miss.
      const activeTenant = getSelectedTenant();
      const headers = {
        "Content-Type": "application/json",
        ...(activeTenant ? { [ACTIVE_TENANT_HEADER]: activeTenant } : {}),
        ...(options.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}`, ...tenantHeaders() } : {})
      };

      const finalUrl =
        url.startsWith('http')
          ? url
          : `${getBackendUrl()}${url}`;

      let response = await fetch(finalUrl, {
        ...options,
        headers,
        credentials: 'include'
      });

      // retry with forced refresh
      if (response.status === 401 && currentUser) {
        const newToken = await currentUser.getIdToken(true);

        const retryHeaders = {
          "Content-Type": "application/json",
          ...(options.headers || {}),
          Authorization: `Bearer ${newToken}`, ...tenantHeaders()
        };

        response = await fetch(finalUrl, {
          ...options,
          headers: retryHeaders,
          credentials: 'include'
        });

        // A freshly-minted token that the server STILL rejects is a conclusive authentication
        // failure — the session is revoked or expired, and no further retry can recover it.
        // Returning the 401 alone left the shell painting a signed-in user whose every request
        // failed, with no indication that the fix was to sign in again. Invalidate once so the
        // route guard sends them to login. Single-flight: a burst of 401s must not sign out
        // repeatedly or fight the redirect.
        if (response.status === 401 && !sessionInvalidationRef.current) {
          sessionInvalidationRef.current = true;
          try { await logout(); } finally { sessionInvalidationRef.current = false; }
        }
      }

      return response;

    } catch (error) {
      console.error("Authenticated request error:", error);
      throw error;
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await makeAuthenticatedRequest('/api/v1/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileData || {})
      });

      const data = await response.json();
      const updatedUser = data?.user || data || null;

      if (updatedUser) {
        setUser(updatedUser);
        localStorage.setItem('crm_user_data', JSON.stringify(updatedUser));
      }

      return { success: true, data };
    } catch (error) {
      console.error('Update profile error:', error);
      return { success: false, error: error.message || 'Profile update failed' };
    }
  };

  const setupBiometric = async (biometricData) => {
    try {
      const response = await makeAuthenticatedRequest('/api/v1/security/setup-biometric', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(biometricData || {})
      });

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Setup biometric error:', error);
      return { success: false, error: error.message || 'Biometric setup failed' };
    }
  };

  const registerDeviceToken = async (deviceToken, deviceId, platform) => {
    try {
      const response = await makeAuthenticatedRequest('/api/v1/notifications/device-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_token: deviceToken,
          device_id: deviceId || 'web_device',
          platform: platform || 'web'
        })
      });

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Register device token error:', error);
      return { success: false, error: error.message || 'Device token registration failed' };
    }
  };

  // Function to revoke business access (for when staff is removed)
  const revokeBusinessAccess = async (staffEmail, businessId) => {
    try {
      // Do not log the staff email or tenant id (PII / internal identifier).
      console.log('[CRMAuth] revokeBusinessAccess called');

      // Mock API call
      /*
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/businesses/${businessId}/staff/${staffEmail}/revoke`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        }
      });
      */

      return { success: true, convertedToCustomer: true };
    } catch (error) {
      console.error('Error revoking business access:', error);
      return { success: false, error: error.message };
    }
  };

  // ── IDENTITY STABILITY (2026-09-19) — RENDER BEHAVIOUR ONLY, NO AUTH BEHAVIOUR CHANGE ──────────────
  // Every handler above is redefined on each render, and `value` was a fresh object literal, so the
  // context value changed identity on EVERY provider render. Consumers that legitimately depend on a
  // handler then re-ran: BusinessCommercial does
  //     load = useCallback(..., [makeAuthenticatedRequest]);  useEffect(..., [isLoading, load])
  // so each provider re-render re-fired a full Plan & Capabilities load — three requests, one of which
  // was the 12.4 s /me/features call.
  //
  // WHY A FORWARDING REF AND NOT useCallback-WITH-DEPS. These handlers close over Firebase state,
  // confirmationResult, pendingPhoneNumber and setters. Hand-writing a dependency array for each one
  // risks capturing a STALE closure — an auth bug far worse than a re-render. The ref below always
  // forwards to the CURRENT render's function, so the call is byte-for-byte the same work it is today;
  // only the function's IDENTITY is frozen. Arguments and return values pass through untouched.
  const _handlers = useRef({});
  _handlers.current = {
    registerPhoneNumber, loginPhoneNumber, verifyOTP, resendOTP, logout, updateProfile,
    setupBiometric, registerDeviceToken, makeAuthenticatedRequest, refreshAccessToken,
    getBackendUrl, revokeBusinessAccess,
  };
  const stableHandlers = useMemo(() => ({
    registerPhoneNumber: (...a) => _handlers.current.registerPhoneNumber(...a),
    loginPhoneNumber: (...a) => _handlers.current.loginPhoneNumber(...a),
    verifyOTP: (...a) => _handlers.current.verifyOTP(...a),
    resendOTP: (...a) => _handlers.current.resendOTP(...a),
    logout: (...a) => _handlers.current.logout(...a),
    updateProfile: (...a) => _handlers.current.updateProfile(...a),
    setupBiometric: (...a) => _handlers.current.setupBiometric(...a),
    registerDeviceToken: (...a) => _handlers.current.registerDeviceToken(...a),
    makeAuthenticatedRequest: (...a) => _handlers.current.makeAuthenticatedRequest(...a),
    refreshAccessToken: (...a) => _handlers.current.refreshAccessToken(...a),
    getBackendUrl: (...a) => _handlers.current.getBackendUrl(...a),
    revokeBusinessAccess: (...a) => _handlers.current.revokeBusinessAccess(...a),
  }), []);

  // Now the value changes identity ONLY when real auth STATE changes — which is exactly when a consumer
  // should re-run. The four state fields are the complete set of non-function members.
  const value = useMemo(() => ({
    user,
    isAuthenticated,
    isLoading,
    accessToken,
    ...stableHandlers,
  }), [user, isAuthenticated, isLoading, accessToken, stableHandlers]);

  return (
    <CRMAuthContext.Provider value={value}>
      <div id="crm-recaptcha-container" style={{ display: 'none' }} />
      {/* P0 auth-recovery: visible reCAPTCHA fallback mount (shown only when invisible can't mint a token) */}
      <div
        id="crm-recaptcha-visible"
        style={{ position: 'fixed', bottom: '16px', right: '16px', zIndex: 99999, display: 'none', background: '#fff', padding: '12px', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.25)' }}
      />
      {children}
    </CRMAuthContext.Provider>
  );
};

export default CRMAuthContext;